import React, { FunctionComponent, ReactElement, lazy, Suspense, useEffect, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { getAcademyMicrofrontendUrl } from "@/js/utils/getMicrofrontendUrl";
import { loadDynamicRemote } from "@/utils/loadDynamicRemote";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ShadowDOMWrapper } from "@/components/ShadowDOMWrapper";
import posthog from "posthog-js";
import { useAcademyMFEContextStore } from "@/stores/v2/academyMFEContextStore";

interface AcademyModules {
	ClassroomDrawerButton?: React.ComponentType<ClassroomDrawerButtonProps>;
}

export interface FeedbackData {
	rating: number;
	comment: string;
	trainingName: string;
	contentType: string;
}

interface ClassroomDrawerButtonProps {
	token: string;
	deviceId: string;
	masquerade?: boolean;
	region?: string;
	onFeedbackSubmit: (feedback: FeedbackData) => void;
	initialTrainingId?: string;
	initialTrainingVersion?: string;
	username?: string;
	posthog?: typeof posthog;
	product?: string;
	/** Normalised i18next locale (`en`, `es`, …) for Academy MFE (CM1 i18n strategy). */
	locale?: string;
}

// Fallback component for when academy MFE fails to load
const AcademyButtonFallback = () => (
	<Box p={4}>
		<Text>Failed to load Academy button. Please try refreshing the page.</Text>
	</Box>
);

// Load Academy micro frontend dynamically
const DynamicAcademyButton = lazy(() => {
	const academyModuleRemoteUrl = getAcademyMicrofrontendUrl(window.location.hostname) + "?nocache=" + new Date().getTime();

	return loadDynamicRemote<AcademyModules>(academyModuleRemoteUrl, "QoriaAcademy", "./QoriaAcademy", "vite", "esm")
		.then(async (academyModule) => {
			const component = academyModule?.ClassroomDrawerButton;

			// Return a wrapper component that properly renders the academy button component
			const WrappedComponent = (props: ClassroomDrawerButtonProps) => {
				try {
					// Create an isolated error boundary for the academy component
					if (component) {
						return <ErrorBoundary fallback={<AcademyButtonFallback />}>{React.createElement(component, props)}</ErrorBoundary>;
					}
					return <AcademyButtonFallback />;
				} catch (error) {
					console.error("Academy Button Wrapper: Error rendering component:", error);
					return <AcademyButtonFallback />;
				}
			};

			return {
				default: WrappedComponent,
			};
		})
		.catch((error) => {
			console.error("Failed to load Academy MFE:", error);
			return { default: AcademyButtonFallback };
		});
});

const handleFeedbackSubmit = (feedbackData: FeedbackData) => {
	posthog.capture("user_feedback", {
		product: "Classwize Academy",
		rating: feedbackData.rating,
		comment: feedbackData.comment,
		trainingName: feedbackData.trainingName,
		contentType: feedbackData.contentType,
	});
};

const AcademyButton: FunctionComponent = (): ReactElement => {
	const { i18n } = useTranslation();
	const userSessionStore = useUserSessionStore();
	const { trainingParams, clearTrainingParams } = useAcademyMFEContextStore();
	const isMasquerade = useUserSessionStore((state) => state.masquerade);

	// Local state to safely hold the initial parameters for the MFE
	const [initialParams, setInitialParams] = useState<{
		initialTrainingId?: string;
		initialTrainingVersion?: string;
	} | null>(null);

	// When training params arrive, latch them locally
	useEffect(() => {
		if (trainingParams?.initialTrainingId && trainingParams?.initialTrainingVersion) {
			setInitialParams({
				initialTrainingId: trainingParams.initialTrainingId,
				initialTrainingVersion: trainingParams.initialTrainingVersion,
			});
			clearTrainingParams();
		}
	}, [trainingParams, clearTrainingParams]);

	const region = userSessionStore.deviceinfo?.region;

	return (
		<ErrorBoundary fallback={<AcademyButtonFallback />}>
			<Suspense fallback={<Box p={4}>Loading Academy...</Box>}>
				<ShadowDOMWrapper emotionCacheKey="academy-button-shadow-dom">
					<DynamicAcademyButton
						key={initialParams?.initialTrainingId}
						token={userSessionStore.getToken() || ""}
						deviceId={userSessionStore.getActiveDevice() || ""}
						region={region}
						masquerade={isMasquerade}
						onFeedbackSubmit={handleFeedbackSubmit}
						initialTrainingId={initialParams?.initialTrainingId}
						initialTrainingVersion={initialParams?.initialTrainingVersion}
						username={userSessionStore.getActiveUser()}
						posthog={posthog}
						product="classwize"
						locale={i18n.resolvedLanguage ?? i18n.language}
					/>
				</ShadowDOMWrapper>
			</Suspense>
		</ErrorBoundary>
	);
};

export default AcademyButton;
